import React, { useRef, useEffect, useState } from 'react';
import useSound from 'use-sound';
import DomPurify from 'dompurify';
import { uid } from 'react-uid';

import sfxUserPost from '../assets/sfx/chat_sfx_post.mp3';
import sfxBotResponse from '../assets/sfx/chat_sfx_response.mp3';
import { Message, MessageSender } from '../types/shared';

export interface HistoryProps extends MessageSender {
  chatId: string;
  messages: Message[];
}

function History(props: HistoryProps): JSX.Element {
  const { chatId, messages, sendMessage } = props;
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const message = messages[messages.length - 1];
  const feedbackIntentString = 'FEEDBACK V2';
  const [feedbackTimer, setFeedbackTimer] = useState<number | undefined>(undefined);
  const [playUserPost] = useSound(sfxUserPost);
  const [playBotResponse] = useSound(sfxBotResponse);

  const scrollToBottom = (): void => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (message) {
      if (message.sender === 'self' && message.textBody !== feedbackIntentString) {
        clearTimeout(feedbackTimer);
        playUserPost();
      } else if (message.sender === 'bot') {
        playBotResponse();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    if (message) {
      const isBotMessage = message.sender === 'bot';
      const { errorMessage } = message;
      const isQuestion = message.textBody && message.textBody.includes('?');
      // Check that the intent name starts with "FEEDBACK V2" - if so, this is a feedback response that should not trigger the feedback question
      const isFeedbackResponse =
        message.dialogflowIntentDisplayName &&
        /^FEEDBACK V2/.test(message.dialogflowIntentDisplayName);
      const askForFeedback = isBotMessage && !errorMessage && !isFeedbackResponse && !isQuestion;

      if (!isFeedbackResponse) {
        scrollToBottom();
      }

      if (askForFeedback) {
        clearTimeout(feedbackTimer);
        setFeedbackTimer(
          window.setTimeout(() => {
            // this error is handled in ChatWindowContainer useMutation call
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            sendMessage({
              variables: {
                id: chatId,
                textBody: feedbackIntentString,
              },
            });
          }, 2000)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const onClick = (response: string): void => {
    // this error is handled in ChatWindowContainer useMutation call
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    sendMessage({
      variables: {
        id: chatId,
        textBody: response,
      },
    });
  };

  let responseButtons;
  if (messages.length && messages[messages.length - 1].responses) {
    responseButtons = messages[messages.length - 1].responses.map((response, index) => {
      return (
        <button
          type="button"
          data-testid="bot-button-response"
          className="Response-button"
          key={uid(index)}
          onClick={(): void => onClick(response)}
        >
          {response}
        </button>
      );
    });
  }

  return (
    <div className="History" aria-live="polite" data-testid="history-section">
      {messages.map(({ sender, textBody, timeStamp }) => {
        const messageType = sender === 'self' ? 'Outgoing-message' : 'Incoming-message';
        const dataTestId = sender === 'self' ? 'user-message' : 'bot-response';

        if (textBody && textBody !== feedbackIntentString) {
          // Empty <p> tags are created for rich text mustache references that live on their own line in Kentico. Remove them.
          const textBodyNoEmptyParagraphTags = textBody.replace(/<p><\/p>/, '');
          // Kentico prepends rich text URL hyperlinks with http://[[mustacheVar]]. Remove them.
          const textBodyNoPrependedHttpMustache = textBodyNoEmptyParagraphTags.replace(
            'http://[[',
            '[['
          );
          // Sanitize the remaining markup.
          const sanitizedTextBody = DomPurify.sanitize(textBodyNoPrependedHttpMustache, {
            ADD_ATTR: ['target'],
          });

          return (
            <div
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
              key={timeStamp}
              data-testid={dataTestId}
              className={messageType}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedTextBody }}
            />
          );
        }

        return null;
      })}
      {responseButtons}
      <div ref={messagesEndRef} />
    </div>
  );
}

export default History;
